import { useCallback, useState } from 'react';

export type TourState = 'unset' | 'phaseB-pending' | 'seen';

const VALID_STATES: ReadonlyArray<TourState> = ['unset', 'phaseB-pending', 'seen'];

function storageKey(tourKey: string): string {
  return `farchat.tour.${tourKey}.state.v1`;
}

function lastIndexKey(tourKey: string): string {
  return `farchat.tour.${tourKey}.lastIndex.v1`;
}

function readState(tourKey: string): TourState {
  try {
    const raw = localStorage.getItem(storageKey(tourKey));
    if (raw && (VALID_STATES as ReadonlyArray<string>).includes(raw)) {
      return raw as TourState;
    }
  } catch {
    // localStorage may throw in restricted environments (private mode, etc.)
  }
  return 'unset';
}

function readLastIndex(tourKey: string): number | null {
  try {
    const raw = localStorage.getItem(lastIndexKey(tourKey));
    if (raw === null) return null;
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && Number.isInteger(parsed) && parsed >= 0) {
      return parsed;
    }
  } catch {
    // ignore storage failures
  }
  return null;
}

export interface UseTourSeenResult {
  state: TourState;
  lastIndex: number | null;
  markPhaseAComplete(lastIndex: number): void;
  markSeen(): void;
  setLastIndex(index: number): void;
  reset(): void;
}

/**
 * Manages a tour's seen-state in localStorage with a versioned key.
 * Reads synchronously on first render so callers can branch immediately.
 *
 * Also tracks `lastIndex`: the step index where the user paused/exited.
 * Stored under a separate versioned key so it can be cleared independently
 * of the state value.
 */
export function useTourSeen(tourKey: string): UseTourSeenResult {
  const [state, setState] = useState<TourState>(() => readState(tourKey));
  const [lastIndex, setLastIndexState] = useState<number | null>(() =>
    readLastIndex(tourKey)
  );

  const markPhaseAComplete = useCallback(
    (idx: number) => {
      try {
        localStorage.setItem(storageKey(tourKey), 'phaseB-pending');
        localStorage.setItem(lastIndexKey(tourKey), String(idx));
      } catch {
        // ignore storage failures
      }
      setState('phaseB-pending');
      setLastIndexState(idx);
    },
    [tourKey]
  );

  const markSeen = useCallback(() => {
    try {
      localStorage.setItem(storageKey(tourKey), 'seen');
      localStorage.removeItem(lastIndexKey(tourKey));
    } catch {
      // ignore storage failures
    }
    setState('seen');
    setLastIndexState(null);
  }, [tourKey]);

  const setLastIndex = useCallback(
    (idx: number) => {
      try {
        localStorage.setItem(lastIndexKey(tourKey), String(idx));
      } catch {
        // ignore storage failures
      }
      setLastIndexState(idx);
    },
    [tourKey]
  );

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(storageKey(tourKey));
      localStorage.removeItem(lastIndexKey(tourKey));
    } catch {
      // ignore storage failures
    }
    setState('unset');
    setLastIndexState(null);
  }, [tourKey]);

  return { state, lastIndex, markPhaseAComplete, markSeen, setLastIndex, reset };
}
